import { useEffect, useState, useRef } from 'react';
import { itineraryGenerationApi } from '../services/api';
import { getFactsForCountry } from '../data/destinationFunFacts';

/**
 * Custom hook for polling itinerary generation status
 * Polls the backend every 2 seconds to get job progress
 *
 * Usage:
 * const {
 *   progress,
 *   skeleton,
 *   rotatingMessage,
 *   isComplete,
 *   error,
 *   itineraryId
 * } = useItineraryGenerationSocket(jobId);
 */
const useItineraryGenerationSocket = (jobId) => {
  const [progress, setProgress] = useState(null);
  const [skeleton, setSkeleton] = useState(null);
  const [rotatingMessage, setRotatingMessage] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [itineraryId, setItineraryId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const pollIntervalRef = useRef(null);
  const messageIntervalRef = useRef(null);
  const messageIndexRef = useRef(0);
  const skeletonCreatedRef = useRef(false);

  useEffect(() => {
    if (!jobId) return;

    setIsConnected(true);

    // Function to poll job status
    const pollStatus = async () => {
      try {
        const response = await itineraryGenerationApi.getJobStatus(jobId);
        const status = response.data;

        // Build progress object
        const progressData = {
          jobId: status.jobId,
          state: status.state,
          displayName: status.displayName,
          description: status.description,
          progressPercentage: status.progressPercentage,
          elapsedSeconds: status.elapsedSeconds,
          isComplete: status.isComplete
        };

        setProgress(progressData);

        // If we're at LLM_GENERATION_STARTED, create skeleton and start rotating messages
        if (status.state === 'LLM_GENERATION_STARTED' && !skeletonCreatedRef.current) {
          skeletonCreatedRef.current = true;

          // Create skeleton preview using actual job parameters
          const days = status.durationDays || 7;
          setSkeleton({
            jobId: status.jobId,
            country: status.country || 'Your destination',
            durationDays: days,
            estimatedDays: Array.from({ length: days }, (_, i) => ({
              dayNumber: i + 1,
              title: `Day ${i + 1}`,
              activitiesCount: 3
            }))
          });

          // Start rotating messages using country-specific fun facts
          if (!messageIntervalRef.current) {
            const facts = getFactsForCountry(status.country);
            // Set first message immediately
            setRotatingMessage(facts[0]);
            messageIndexRef.current = 0;

            // Then rotate every 5 seconds
            messageIntervalRef.current = setInterval(() => {
              messageIndexRef.current = (messageIndexRef.current + 1) % facts.length;
              setRotatingMessage(facts[messageIndexRef.current]);
            }, 5000);
          }
        }

        // Check if complete
        if (status.isComplete) {
          setIsComplete(true);

          if (status.itineraryId) {
            setItineraryId(status.itineraryId);
          }

          if (status.errorMessage) {
            setError(status.errorMessage);
          }

          // Stop polling
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }

          // Stop rotating messages
          if (messageIntervalRef.current) {
            clearInterval(messageIntervalRef.current);
            messageIntervalRef.current = null;
          }
        }
      } catch (err) {
        setError('Failed to fetch job status. Please try again.');
        setIsConnected(false);
      }
    };

    // Poll immediately
    pollStatus();

    // Then poll every 2 seconds
    pollIntervalRef.current = setInterval(pollStatus, 2000);

    // Cleanup function
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    };
  }, [jobId]);

  return {
    progress,
    skeleton,
    rotatingMessage,
    isComplete,
    error,
    itineraryId,
    isConnected,
  };
};

export default useItineraryGenerationSocket;
